// Prepare purchase order (P3.1; 01-SPEC §3 step 4, 08-DOCUMENTS). Pure rules, unit-tested: line
// totals, VAT at the office rate and the payment schedule, all in integer minor units (halalas,
// fils, piastres). Large values are multiplied as BigInt so nothing loses precision.

import { z } from 'zod';
import { UNITS } from '../pr/form';

export const PO_LINE_TYPES = ['one-off', 'monthly', 'milestone'] as const;
export type PoLineType = (typeof PO_LINE_TYPES)[number];

export const PAYMENT_TERMS = [
  'advance100',
  'split50',
  'split25',
  'delivery100',
  'net30',
  'custom',
] as const;
export type PaymentTerm = (typeof PAYMENT_TERMS)[number];

export const MILESTONES = ['advance', 'delivery', 'net30', 'milestone'] as const;
export type Milestone = (typeof MILESTONES)[number];

export interface ScheduleRow {
  milestone: Milestone;
  /** Free text for a custom milestone ("On installation"). */
  label: string;
  pct: number;
}

/** The presets of 01-SPEC (P3.1): full advance, 50/50, 25/75, 100% on delivery, Net 30. */
export const PRESETS: Record<Exclude<PaymentTerm, 'custom'>, ScheduleRow[]> = {
  advance100: [{ milestone: 'advance', label: '', pct: 100 }],
  split50: [
    { milestone: 'advance', label: '', pct: 50 },
    { milestone: 'delivery', label: '', pct: 50 },
  ],
  split25: [
    { milestone: 'advance', label: '', pct: 25 },
    { milestone: 'delivery', label: '', pct: 75 },
  ],
  delivery100: [{ milestone: 'delivery', label: '', pct: 100 }],
  net30: [{ milestone: 'net30', label: '', pct: 100 }],
};

export const MAX_PO_LINES = 50;
export const MAX_SCHEDULE_ROWS = 6;
export const MAX_MONTHS = 60;
/** 10 billion in major units: keeps every sum far inside JavaScript's safe integers. */
export const MAX_TOTAL_MINOR = 1_000_000_000_000;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

const decimals = (v: number, places: number) => {
  const scaled = v * 10 ** places;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
};

export const poLineSchema = z.strictObject({
  description: text(500).min(1, 'Required'),
  qty: z
    .number()
    .positive('More than 0')
    .max(1_000_000)
    .refine((v) => decimals(v, 3), 'Up to 3 decimals'),
  unit: z.enum(UNITS),
  type: z.enum(PO_LINE_TYPES),
  /** Months for a monthly line; 1 otherwise. */
  months: z.number().int().min(1).max(MAX_MONTHS).default(1),
  /** Unit price excluding VAT, in major units (per month for a monthly line). */
  unitPrice: z
    .number()
    .nonnegative('Prices cannot be negative')
    .max(100_000_000)
    .refine((v) => decimals(v, 2), 'Up to 2 decimals'),
});
export type PoLineInput = z.infer<typeof poLineSchema>;

export const scheduleRowSchema = z.strictObject({
  milestone: z.enum(MILESTONES),
  label: text(80).default(''),
  pct: z.number().int().min(1).max(100),
});

export const poDraftSchema = z.strictObject({
  /** ETag of the PO draft when it already exists; empty for the first save. */
  etag: z.string().max(200).default(''),
  quoteRef: text(60).default(''),
  agreementNo: text(60).default(''),
  projectCode: text(60).default(''),
  receiver: text(120).default(''),
  phone: z
    .string()
    .trim()
    .max(30)
    .regex(/^(\+?[0-9][0-9 ()-]{5,28})?$/, 'Use digits, spaces and an optional +')
    .default(''),
  email: z.union([z.literal(''), z.email('Use a valid email').max(200)]).default(''),
  lines: z.array(poLineSchema).min(1, 'Add at least one line').max(MAX_PO_LINES),
  paymentTerm: z.enum(PAYMENT_TERMS),
  /** Used only with the custom term. */
  schedule: z.array(scheduleRowSchema).max(MAX_SCHEDULE_ROWS).default([]),
  terms: text(2000).default(''),
  internalNote: text(2000).default(''),
});
export type PoDraftInput = z.infer<typeof poDraftSchema>;

export const poSubmitSchema = z.strictObject({ etag: z.string().min(1).max(200) });

/** Back to quotes from a PO draft (D112): the reason is kept on the request's history. */
export const poReopenSchema = z.strictObject({
  reason: z
    .string()
    .trim()
    .min(1, 'Say why')
    .max(2000)
    .regex(/^[^<>]*$/, 'Remove < and >'),
});

/** Issue now, CFO signs later (P3.3). */
export const poSendFirstSchema = poSubmitSchema;

/** Major units (2 decimals) to integer minor units. */
export const toMinor = (major: number) => Math.round(major * 100);

/** VAT rate in percent (15, 5, 14) to basis points (1500, 500, 1400). */
export const basisPoints = (ratePct: number) => Math.round(ratePct * 100);

/** Half-up division of non-negative BigInts. */
const divRound = (n: bigint, d: bigint) => (n * 2n + d) / (2n * d);

export interface PoLine {
  type: PoLineType;
  qty: number;
  months: number;
  /** Minor units. */
  unitPrice: number;
}

/** qty × unit price (× months for a monthly line), rounded once to the minor unit. */
export function poLineTotal(line: PoLine): number {
  const milliQty = BigInt(Math.round(line.qty * 1000));
  const months = BigInt(line.type === 'monthly' ? line.months : 1);
  return Number(divRound(milliQty * BigInt(line.unitPrice) * months, 1000n));
}

/** VAT on the subtotal at the office rate, half up (one rounding, on the document total). */
export function vatOn(subtotal: number, ratePct: number): number {
  return Number(divRound(BigInt(subtotal) * BigInt(basisPoints(ratePct)), 10000n));
}

export interface PoTotals {
  lineTotals: number[];
  /** Per month, over the monthly lines (0 without any). */
  monthly: number;
  subtotal: number;
  vatRate: number;
  vat: number;
  total: number;
}

export function poTotals(lines: PoLine[], vatRatePct: number): PoTotals {
  const lineTotals = lines.map(poLineTotal);
  const monthly = lines
    .filter((l) => l.type === 'monthly')
    .reduce((sum, l) => sum + poLineTotal({ ...l, type: 'one-off' }), 0);
  const subtotal = lineTotals.reduce((a, b) => a + b, 0);
  const vat = vatOn(subtotal, vatRatePct);
  return { lineTotals, monthly, subtotal, vatRate: vatRatePct, vat, total: subtotal + vat };
}

/** The schedule a payment term stands for (the custom rows as given). */
export function scheduleFor(term: PaymentTerm, custom: ScheduleRow[]): ScheduleRow[] {
  return term === 'custom' ? custom : PRESETS[term];
}

/** Why a schedule is refused, or null. */
export function scheduleProblem(rows: ScheduleRow[]): string | null {
  if (rows.length === 0) return 'Add at least one payment';
  if (rows.length > MAX_SCHEDULE_ROWS) return `Up to ${MAX_SCHEDULE_ROWS} payments`;
  if (rows.some((r) => r.milestone === 'milestone' && !r.label.trim())) {
    return 'Name each milestone';
  }
  const sum = rows.reduce((s, r) => s + r.pct, 0);
  return sum === 100 ? null : `The payments add up to ${sum}%, not 100%`;
}

/** Each payment's amount; the last one takes the rounding so they add up to the total exactly. */
export function scheduleAmounts(rows: ScheduleRow[], total: number): number[] {
  let left = total;
  return rows.map((r, i) => {
    if (i === rows.length - 1) return left;
    const amount = Number(divRound(BigInt(total) * BigInt(r.pct), 100n));
    left -= amount;
    return amount;
  });
}

/** Problems with a draft that only the totals reveal, by field. */
export function totalsProblems(totals: PoTotals): Record<string, string> {
  const out: Record<string, string> = {};
  if (totals.subtotal <= 0) out.lines = 'The PO total must be more than 0';
  if (totals.total > MAX_TOTAL_MINOR) out.lines = 'The PO total is too large';
  return out;
}

// ---------- API bodies ----------

export interface PoLineView {
  description: string;
  qty: number;
  unit: string;
  type: PoLineType;
  months: number;
  /** Minor units. */
  unitPrice: number;
  total: number;
}

export interface PoScheduleView extends ScheduleRow {
  amount: number;
}

export interface PoDraftView {
  quoteRef: string;
  agreementNo: string;
  projectCode: string;
  receiver: string;
  phone: string;
  email: string;
  lines: PoLineView[];
  paymentTerm: PaymentTerm;
  schedule: PoScheduleView[];
  terms: string;
  internalNote: string;
  totals: PoTotals;
}

export interface PoPageBody {
  prNo: string;
  title: string;
  status: string;
  rush: boolean;
  entity: { code: string; name: string };
  office: { code: string; vatRate: number };
  currency: string;
  supplier: { id: string; name: string };
  quote: {
    id: string;
    ref: string;
    file: string;
    amount: number;
    currency: string;
    exceptionReason: string;
    flagged: boolean;
  };
  delivery: { location: string; address: string };
  /** The saved draft, or one proposed from the request and the chosen quote. */
  draft: PoDraftView;
  saved: boolean;
  etag: string;
  poStatus: string;
  preparedBy: string;
  /** The CFO's remark when the PO came back (returned) for changes. */
  cfoRemark: string;
  /** Why the vertical PO approver returned it (D114). */
  verticalRemark: string;
  /** Send-first (P3.3): why this PO may be issued before the CFO signs, or null. */
  sendFirst: { why: 'rush' | 'small' | null; limit: number; days: number; due: string };
  /** Set once the PO number is issued (send-first). */
  poNo: string;
  canEdit: boolean;
}

export interface PoSavedBody {
  etag: string;
  status: string;
  draft: PoDraftView;
}
